import React, { useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import classnames from 'classnames';

import FilterHeader from '../../webcommon/header/filterheader/FilterHeader';
import Checkbox from '../../webcommon/common/checkbox/Checkbox';

import styles from './Summary.module.scss';
import IdSearch from '../IdSearch/IdSearch';
import {
    impactAnalysisLoaded, setCurrencyToShow, setShowCsaSummary, setShowDefaultMappings,
    copyTransactionId
} from './actions';
import SummaryInfo from './summaryinfo/SummaryInfo';
import {
    getSummaryStatsInfo, getSummaryInfoLoaded, getCurrencyToShow,
    getSummaryGridRows, getSummaryGridColumns, getLoading, getShowCsaSummary,
    getShowDefaultMappings, getCurrentCsaStatus, getCurrentCsaType, getTransactionId
} from './selectors';
import ToggleSwitch from '../../common/gridheader/toggleswitch/ToggleSwitch';
import { getImpactId } from '../impactAnalysisIdSummary/selectors';
import { initialise } from '../impactAnalysisIdSummary/actions';
import summaryIdFilterStyles from '../impactAnalysisIdSummary/FilterHeaderOverride.module.scss';
import MonitoringStatusPill from '../../common/monitoringStatusPill/MonitoringStatusPill';

const Summary = (props) => {
    const {
        readOnly,
        initialise,
        impactAnalysisLoaded,
        setShowCsaSummary
    } = props;
    useEffect(() => {
        if (readOnly) {
            initialise();
        } else {
            impactAnalysisLoaded();
        }
    }, [readOnly, initialise, impactAnalysisLoaded]);

    const toggleSwitch = () => (
        props.currencyToShow === 'EUR' ? props.setCurrencyToShow('USD') : props.setCurrencyToShow('EUR')
    );

    const setShowCsaSummaryMemoized = useCallback((checked) => {
        setShowCsaSummary(checked);
    }, [setShowCsaSummary]);

    const {
        excludePrincipal, excludeShortOption, imPledgeEur, imReceiveEur, imThresholdEur,
        imReceiveUsd, imThresholdUsd, imPledgeUsd, calcCurrencyCollect, calcCurrencyPost,
        monitoringThresholdLevel1Eur, monitoringThresholdLevel2Eur, monitoringThresholdLevel1Usd,
        monitoringThresholdLevel2Usd, imThresholdCollectEur, imThresholdPostEur, imThresholdCollectUsd,
        imThresholdPostUsd
    } = props.summaryStats;

    const title = useMemo(() => `Summary for ${props.id}`, [props.id]);

    const summaryFilterHeader = useMemo(() => classnames({
        [styles.summaryFilterHeader]: true,
        [styles.hidden]: props.readOnly
    }), [props.readOnly]);

    const summaryFilterHeaderDisabled = useMemo(() => classnames({
        [styles.summaryFilterHeader]: true,
        [styles.hidden]: !props.readOnly
    }), [props.readOnly]);

    const filterHeaderText = useMemo(() => classnames({
        [styles.filterHeaderText]: true,
        [styles.noMargin]: props.readOnly
    }), [props.readOnly]);

    const filterHeaderCheckbox = useMemo(() => classnames({
        [styles.filterHeaderCheckbox]: true,
        [styles.noMargin]: props.readOnly
    }), [props.readOnly]);

    return (
        <div className={ styles.impactSummary }>
            <div className={ styles.filterHeaderWrapper }>
                <div className={ summaryFilterHeader }>
                    <FilterHeader
                        fixed
                        title="Summary"
                        id="ImpactAnalysis"
                    />
                </div>
                <div className={ summaryFilterHeaderDisabled }>
                    <FilterHeader
                        fixed
                        title={ title }
                        id="ImpactAnalysisIdSummary"
                        styles={ summaryIdFilterStyles }
                        disabled
                    />
                </div>
                <div className={ styles.summaryItemsWrapper }>
                    <div className={ styles.searchWrapper }>
                        <IdSearch />
                    </div>
                    { props.status && props.readOnly && (
                        <div className={ styles.statusPillWrapper }>
                            <MonitoringStatusPill status={ props.status.status } colour={ props.status.colour } />
                        </div>
                    )}

                    <div className={ styles.checkboxToggleWrapper }>
                        { !props.readOnly && (
                            <>
                                <div className={ filterHeaderText }>
                                    Show CSA Summary
                                </div>
                                <div className={ filterHeaderCheckbox }>
                                    <Checkbox
                                        checked={ props.showCsaSummary }
                                        onChange={ setShowCsaSummaryMemoized }
                                    />
                                </div>
                                <div className={ styles.toggleSwitch }>
                                    <ToggleSwitch
                                        optionLeft="EUR"
                                        optionRight="USD"
                                        toggleIsLeft={ props.currencyToShow === 'EUR' }
                                        toggleSwitch={ toggleSwitch }
                                    />
                                </div>
                            </>
                        )
                        }
                    </div>
                </div>
            </div>

            {props.showCsaSummary && props.summaryInfoLoaded && !props.readOnly
                ? (
                    <SummaryInfo
                        calcCurrencyCollect={ calcCurrencyCollect || '' }
                        calcCurrencyPost={ calcCurrencyPost || '' }
                        excludePrincipal={ excludePrincipal }
                        imPledgeEur={ imPledgeEur }
                        imReceiveEur={ imReceiveEur }
                        imThresholdEur={ imThresholdEur }
                        excludeShortOption={ excludeShortOption }
                        imReceiveUsd={ imReceiveUsd }
                        imThresholdUsd={ imThresholdUsd }
                        imPledgeUsd={ imPledgeUsd }
                        setShowDefaultMappings={ props.setShowDefaultMappings }
                        showDefaultMappings={ props.showDefaultMappings }
                        showEuros={ props.currencyToShow === 'EUR' }
                        summaryGridColumns={ props.summaryGridColumns }
                        summaryGridRows={ props.summaryGridRows }
                        loading={ props.loading }
                        monitoringThresholdLevel1Eur={ monitoringThresholdLevel1Eur }
                        monitoringThresholdLevel2Eur={ monitoringThresholdLevel2Eur }
                        monitoringThresholdLevel1Usd={ monitoringThresholdLevel1Usd }
                        monitoringThresholdLevel2Usd={ monitoringThresholdLevel2Usd }
                        imThresholdCollectEur={ imThresholdCollectEur }
                        imThresholdPostEur={ imThresholdPostEur }
                        imThresholdCollectUsd={ imThresholdCollectUsd }
                        imThresholdPostUsd={ imThresholdPostUsd }
                        status={ props.status }
                        currentType={ props.currentType }
                        transactionId={ props.transactionId }
                        copyTransactionId={ props.copyTransactionId }
                    />
                )
                : null }
        </div>
    );
};

Summary.propTypes = {
    currencyToShow: PropTypes.string,
    loading: PropTypes.bool,
    impactAnalysisLoaded: PropTypes.func.isRequired,
    setCurrencyToShow: PropTypes.func.isRequired,
    setShowCsaSummary: PropTypes.func.isRequired,
    setShowDefaultMappings: PropTypes.func.isRequired,
    showCsaSummary: PropTypes.bool,
    showDefaultMappings: PropTypes.bool,
    summaryGridColumns: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            id: PropTypes.string
        })
    ),
    summaryGridRows: PropTypes.arrayOf(PropTypes.shape({})),
    summaryInfoLoaded: PropTypes.bool,
    summaryStats: PropTypes.shape({
        calcCurrencyCollect: PropTypes.string,
        calcCurrencyPost: PropTypes.string,
        excludePrincipal: PropTypes.string,
        excludeShortOption: PropTypes.string,
        imPledgeEur: PropTypes.number,
        imReceiveEur: PropTypes.number,
        imThresholdEur: PropTypes.number,
        imReceiveUsd: PropTypes.number,
        imPledgeUsd: PropTypes.number,
        imThresholdUsd: PropTypes.number,
        monitoringThresholdLevel1Eur: PropTypes.number,
        monitoringThresholdLevel2Eur: PropTypes.number,
        monitoringThresholdLevel1Usd: PropTypes.number,
        monitoringThresholdLevel2Usd: PropTypes.number,
        imThresholdCollectEur: PropTypes.number,
        imThresholdPostEur: PropTypes.number,
        imThresholdCollectUsd: PropTypes.number,
        imThresholdPostUsd: PropTypes.number,
    }),
    location: PropTypes.shape({ search: PropTypes.string }).isRequired,
    status: PropTypes.objectOf(PropTypes.string),
    currentType: PropTypes.string,
    transactionId: PropTypes.string,
    copyTransactionId: PropTypes.func.isRequired,
    id: PropTypes.string,
    readOnly: PropTypes.bool,
    initialise: PropTypes.func.isRequired
};

Summary.defaultProps = {
    currencyToShow: 'EUR',
    loading: false,
    showCsaSummary: true,
    showDefaultMappings: false,
    summaryGridColumns: [],
    summaryGridRows: [],
    summaryInfoLoaded: false,
    summaryStats: {},
    status: null,
    currentType: '',
    transactionId: '',
    readOnly: false,
    id: ''
};

const mapStateToProps = state => ({
    currencyToShow: getCurrencyToShow(state),
    loading: getLoading(state),
    showCsaSummary: getShowCsaSummary(state),
    showDefaultMappings: getShowDefaultMappings(state),
    summaryGridColumns: getSummaryGridColumns(state),
    summaryGridRows: getSummaryGridRows(state),
    summaryInfoLoaded: getSummaryInfoLoaded(state),
    summaryStats: getSummaryStatsInfo(state),
    status: getCurrentCsaStatus(state),
    currentType: getCurrentCsaType(state),
    transactionId: getTransactionId(state),
    id: getImpactId(state)

});

const mapDispatchToProps = {
    setCurrencyToShow,
    setShowCsaSummary,
    setShowDefaultMappings,
    impactAnalysisLoaded,
    copyTransactionId,
    initialise
};

export default withRouter(connect(mapStateToProps,
    mapDispatchToProps)(Summary));

export { Summary as SummaryUnconnected };
